// aiim.datasets
import Dataset from "./Dataset";
import FieldNames from "./FieldNames";
import QueryAll from "../../spaceplanner/base/QueryAll";

// aiim.util
import { findFieldName, getAttributeValue } from "../util/aiimUtil";
import ReservationsErrorHandler from "../util/ReservationsErrorHandler";

// context
import Context from "../../context/Context";
import * as dateUtil from "../../components/main/Events/dateUtil";
import * as selectionUtil from "../../aiim/util/selectionUtil";

// main.More.BookWorkspace.WorkspaceReservation
import {
  makeCurrentTimeRange,
  makeDefExpClause,
  setReservationsDefExp
} from "../../components/main/More/Actions/BookWorkspace/WorkspaceReservation/reservationsLayerUtil";


export default class Reservations extends Dataset {
  name = "reservations";

  // reservedByUsernameField = FieldNames.RESERVED_BY_USERNAME;
  reservedForUsernameField = FieldNames.RESERVED_FOR_USERNAME;
  reservedForFullNameField = FieldNames.RESERVED_FOR_FULL_NAME;
  stateField = FieldNames.STATE;
  startTimeField = FieldNames.START_TIME;
  endTimeField = FieldNames.END_TIME;
  levelIdField = FieldNames.LEVEL_ID;
  unitIdField = FieldNames.UNIT_ID;
  unitNameField = FieldNames.UNIT_NAME;
  allDayField = FieldNames.ALL_DAY;
  checkInTimeField = FieldNames.CHECK_IN_TIME;
  checkOutTimeField = FieldNames.CHECK_OUT_TIME;

  // Optional fields
  titleField = null;
  descriptionField = null;

  // Error handler
  errorHandler;

  currentTimeInterval;
  refreshInterval;
  previousVisibility;

  constructor(refreshInterval) {
    super();
    this.refreshInterval = refreshInterval;
    this.setCurrentTimeInterval();
  }

  /**
   * Properties for time-awareness, and disabling identify on the layer
   */
  setLayerProperties() {
    const { start, end } = makeCurrentTimeRange();
    this.layer2D.definitionExpression = makeDefExpClause(start, end);
    this.layer2D.popupEnabled = false;
    this.layer2D.xtnHitTestDisabled = true;
    this.layer2D.useViewTime = false;
  }

  checkSchema() {
    this.errorHandler = new ReservationsErrorHandler(this);
    const promise = new Promise((resolve, reject) => {
      if (this.layer2D) {
        this.setLayerProperties();

        const layer = this.layer2D;
        if (layer.floorInfo && layer.floorInfo.floorField) {
          this.levelIdField = layer.floorInfo.floorField;
        }

        layer.when(() => {
          // this.checkFieldNameProperty("reservedByUsernameField", layer.fields);
          this.checkFieldNameProperty("reservedForUsernameField", layer.fields);
          this.checkFieldNameProperty("reservedForFullNameField", layer.fields);
          this.checkFieldNameProperty("stateField", layer.fields);
          this.checkFieldNameProperty("startTimeField", layer.fields);
          this.checkFieldNameProperty("endTimeField", layer.fields);
          this.checkFieldNameProperty("levelIdField", layer.fields);
          this.checkFieldNameProperty("unitIdField", layer.fields);
          this.checkFieldNameProperty("unitNameField", layer.fields);
          this.checkFieldNameProperty("allDayField", layer.fields);
          this.checkFieldNameProperty("checkInTimeField", layer.fields);
          this.checkFieldNameProperty("checkOutTimeField", layer.fields);

          // Optional fields
          this.titleField = findFieldName(layer.fields, FieldNames.TITLE);
          this.descriptionField = findFieldName(layer.fields, FieldNames.DESCRIPTION);

          resolve();
        }).catch(ex => {
          console.warn("Failed to load dataset layer:", layer.title);
          console.error(ex);
          resolve();
        });
      } else {
        resolve();
      }
    });
    return promise;
  }

  canUserMakeReservations() {
    const editingEnabled = this.isLayerEditable();
    const supportsRequiredOperations = this.layerSupportsRequiredOperations();
    const userCanEdit = this.canUserEdit();
    return editingEnabled && supportsRequiredOperations && userCanEdit;
  }

  isLayerEditable() {
    const layer = this.layer2D;
    if (!layer) {
      return false;
    }

    const editingEnabled = layer.editingEnabled;
    return editingEnabled;
  }

  layerSupportsRequiredOperations() {
    const layer = this.layer2D;
    if (!layer) {
      return false;
    }

    const supportedOperations = layer.capabilities && layer.capabilities.operations;
    const supportsAdd = supportedOperations && supportedOperations.supportsAdd;
    const supportsDelete = supportedOperations && supportedOperations.supportsDelete;
    const supportsUpdate = supportedOperations && supportedOperations.supportsUpdate;
    return supportsAdd && supportsDelete && supportsUpdate;
  }

  canUserEdit() {
    const user = Context.getInstance().user;
    if (!user) {
      return false;
    }

    const userCanEdit =
      user.isAdmin() ||
      user.isPublisher() ||
      user.isDataEdit() ||
      user.isFullDataEdit();
    return userCanEdit;
  }

  getSource() {
    return Context.getInstance().aiim.datasets.categories.findSourceByKey("Reservations");
  }

  query(where) {
    if (!this.url) return Promise.resolve();
    const url = Context.checkMixedContent(this.url);
    const lib = Context.getInstance().lib;
    const task = new lib.esri.QueryTask({url: url});
    const query = new lib.esri.Query();
    query.outFields = ["*"];
    query.returnGeometry = true;
    query.returnZ = true;
    query.where = where || "1=1";
    return task.execute(query);
  }

  setCurrentTimeInterval() {
    if (this.currentTimeInterval) {
      return;
    }

    const ms = this.refreshInterval ? this.refreshInterval * 60000 : 30000;
    this.currentTimeInterval = setInterval(() => {
      if (this.layer2D) {
        const { start, end } = makeCurrentTimeRange();
        setReservationsDefExp(start, end);
      }
    }, ms);
  }

  clearCurrentTimeInterval() {
    clearInterval(this.currentTimeInterval);
    this.currentTimeInterval = undefined;
  }

  queryAll(qaopts) {
    if (!this.url) return Promise.resolve();
    const url = Context.checkMixedContent(this.url);
    const query = new Context.instance.lib.esri.Query();
    query.outFields = ["*"];
    query.returnGeometry = true;
    query.returnZ = true;
    query.where = "1=1";
    qaopts = qaopts || {};
    if (qaopts.where) query.where = qaopts.where;
    const qa = new QueryAll();
    return qa.execute(url,query,qaopts);
  }

  queryUnitIds(qaopts) {
    if (!this.url) return Promise.resolve();
    const url = Context.checkMixedContent(this.url);
    const query = new Context.instance.lib.esri.Query();
    const unitIdField = this.unitIdField
    query.outFields = [unitIdField];
    query.returnGeometry = false;
    query.returnZ = false;
    query.where = "1=1";
    qaopts = qaopts || {};
    if (qaopts.where) query.where = qaopts.where;
    const qa = new QueryAll();
    return qa.execute(url,query,qaopts).then(result => {
      //console.log("queryUnitIds",result)
      let index = {}, list = [];
      result.features.forEach(f => {
        let uid = f.attributes[unitIdField];
        if (uid) {
          if (!index[uid]) {
            index[uid] = uid;
            list.push(uid);
          }
        }
      });
      return {
        index: index,
        list: list
      };
    });
  }

  getGoogleData(event) {
    const { attributes } = event;

    const title = getAttributeValue(attributes, FieldNames.TITLE);
    const description = getAttributeValue(attributes, FieldNames.DESCRIPTION);
    const startDate = getAttributeValue(attributes, FieldNames.START_TIME);
    const endDate = getAttributeValue(attributes, FieldNames.END_TIME);
    const allDay = getAttributeValue(attributes, FieldNames.ALL_DAY);
    const location = getAttributeValue(attributes, FieldNames.UNIT_NAME);

    return { title, description, startDate, endDate, allDay, location };
  }

  queryUnitSchedule(unitId,fromDate,toDate) {
    if (!this.url) return Promise.resolve();
    let v = selectionUtil.escSqlQuote(unitId);
    let w = "("+this.unitIdField+" = '"+v+"')";
    let w2 = "("+this.stateField+" IN (0,1,4))"; // pending, approved, checked-in
    w = "("+w+" AND "+w2+")";
    if (fromDate && toDate) {
      let start = dateUtil.getZulu(fromDate);
      let end = dateUtil.getZulu(toDate);
      let w3 = "(("+this.startTimeField + " < TIMESTAMP '" + end+"')";
      w3 += " AND ("+this.endTimeField + " > TIMESTAMP '" + start+"'))";
      w = "("+w+" AND "+w3+")";
    }
    const url = Context.checkMixedContent(this.url);
    const query = new Context.instance.lib.esri.Query();
    query.outFields = ["*"];
    query.orderByFields = [this.startTimeField];
    query.returnGeometry = false;
    query.where = w;

    const reservationInfo = {
      reserved: []
    }
    const qaopts = {
      perFeatureCallback: f => {
        let row = {
          oid: f.attributes[this.layer2D.objectIdField],
          fromDate: f.attributes[this.startTimeField],
          toDate: f.attributes[this.endTimeField],
          title: f.attributes[this.titleField],
          description: f.attributes[this.descriptionField],
          unitName: f.attributes[this.unitNameField],
          levelId: f.attributes[this.levelIdField],
          reservee: f.attributes[this.reservedForFullNameField],
          feature: f
        }
        reservationInfo.reserved.push(row);
      }
    };

    const qa = new QueryAll();
    return qa.execute(url,query,qaopts).then(result => {
      //console.log("reservationInfo",reservationInfo)
      return reservationInfo;
    });
  }
}
